import React, { useContext, useMemo } from 'react';
import {
  Colors,
  ColorValues,
  Flex,
  IconChevronRight,
  RemoteImage,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { useRelatedGoods } from '../../../queries/useRelatedGoods';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import { ScrollContext } from '../../../context/ScrollContext';
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { bridge, isApp, isNil } from 'shared/src/utils';
import { useGoodsSno } from '../../../hooks/useGoodsSno';
import { useLogClickSimilarGoodsMore } from '../hooks/useLogClickSimilarGoodsMore';
import { useLogDisplayRelatedGoods } from '../hooks/useLogDisplayRelatedGoods';

const PADDING_HORIZONTAL = 16;
const ITEM_SPACING = 8;

export const ITEM_SHOW_COUNT = 3;

const GoodsItemView: React.FC<{
  title: string;
  price: string;
  image: string;
  discountPercent: string;
  width: number;
}> = props => {
  const height = useMemo(() => props.width * 1.2, [props.width]);
  return (
    <Flex
      position={'relative'}
      width={props.width}
      height={height}
      borderRadius={4}
      overflow={'hidden'}
    >
      <RemoteImage uri={props.image} width={props.width} height={height} />
      <Flex
        position={'absolute'}
        left={0}
        right={0}
        bottom={0}
        padding={{ horizontal: 8, vertical: 8 }}
      >
        <Flex position={'absolute'} left={0} right={0} bottom={0}>
          <Svg width={props.width} height={height * 0.4}>
            <Rect
              x={0}
              y={0}
              width={props.width}
              height={height * 0.4}
              fill="url(#paint0_linear_5546_5091)"
              fillOpacity="0.6"
            />
            <Defs>
              <LinearGradient
                id="paint0_linear_5546_5091"
                x1={height * 0.4 + 0.5}
                y1="0"
                x2={height * 0.4 + 0.5}
                y2={height * 0.4}
                gradientUnits="userSpaceOnUse"
              >
                <Stop stopOpacity="0" />
                <Stop offset="1" />
              </LinearGradient>
            </Defs>
          </Svg>
        </Flex>
        <Flex.Row gap={4}>
          <Typography type={'subtitle4'} rawColor={ColorValues.pink20}>
            {props.discountPercent}
          </Typography>
          <Typography type={'subtitle4'} color={'content_always_white'}>
            {props.price}
          </Typography>
        </Flex.Row>
        <Typography
          type={'body5'}
          color={'content_always_white'}
          numberOfLines={1}
          ellipsizeMode={'tail'}
        >
          {props.title}
        </Typography>
      </Flex>
    </Flex>
  );
};

export const RelatedGoodsView: React.FC<{ focused: boolean }> = ({
  focused = true,
}) => {
  const { scrollY } = useContext(ScrollContext);
  const goodsSno = useGoodsSno();

  const { data: relatedGoods, isSuccess, isLoading } = useRelatedGoods(focused);
  const { width } = useScreenSize();

  const itemWidth = useMemo(() => {
    return (
      (width - PADDING_HORIZONTAL * 2 - ITEM_SPACING * 2) / ITEM_SHOW_COUNT
    );
  }, [width]);

  const targetItem = useMemo(() => {
    if (!isSuccess) return null;
    if (!relatedGoods) return null;

    if (relatedGoods.item_list.length === 0) return null;
    return relatedGoods.item_list[0];
  }, [isSuccess, relatedGoods]);

  const targetList = useMemo(() => {
    if (isNil(targetItem)) return [];

    if (!focused) {
      return [];
    }

    return targetItem.goods.slice(0, ITEM_SHOW_COUNT);
  }, [targetItem, focused]);

  useLogDisplayRelatedGoods({
    targetList: targetList?.map(item => ({ logging: item.logging })) ?? [],
    isSuccess,
    isLoading,
  });
  const animStyle = useAnimatedStyle(() => {
    if (!scrollY) {
      return {};
    }
    return {
      transform: [
        {
          translateY: interpolate(
            scrollY.value,
            [-width, 0],
            [width * 0.5, 0],
            'clamp',
          ),
        },
      ],
    };
  });

  const logClickSimilarGoodsMore = useLogClickSimilarGoodsMore();

  if (isNil(targetList)) return null;

  // const targetList = relatedGoods.item_list[0];

  return (
    <Flex.Center position={'absolute'} top={0} bottom={0} left={0} right={0}>
      <Animated.View style={animStyle}>
        <Flex.Center gap={20}>
          {isLoading ? (
            <Flex.Row gap={ITEM_SPACING}>
              {Array.from({ length: ITEM_SHOW_COUNT }, (_, i) => i).map(
                item => (
                  <Flex
                    key={item}
                    width={itemWidth}
                    height={itemWidth * 1.2}
                    backgroundColorValue={ColorValues.gray55}
                    borderRadius={4}
                  ></Flex>
                ),
              )}
            </Flex.Row>
          ) : (
            <Flex.Row gap={ITEM_SPACING}>
              {targetList.map(item => (
                <GoodsItemView
                  key={item.sno.toString()}
                  title={item.name}
                  price={item.render.data.price}
                  image={item.image}
                  discountPercent={item.render.data.discount_rate}
                  width={itemWidth}
                />
              ))}
            </Flex.Row>
          )}
          {isApp() && (
            <Flex.Row
              borderWidth={1}
              borderRadius={6}
              borderColor={'white'}
              padding={{ horizontal: 16, vertical: 8 }}
              gap={4}
              alignItems={'center'}
              onPress={() => {
                bridge.openDeeplink(
                  `ably://goods_list/?goods_sno=${goodsSno}&type=${targetItem?.type ?? ''}`,
                );
                logClickSimilarGoodsMore();
              }}
            >
              <Typography type={'subtitle4'} color={'content_always_white'}>
                관련 상품 더보기
              </Typography>
              <IconChevronRight fill={Colors.content_always_white} size={16} />
            </Flex.Row>
          )}
        </Flex.Center>
      </Animated.View>
    </Flex.Center>
  );
};
